import { useQuery } from '@tanstack/react-query'
import { CellContext } from '@tanstack/react-table'
import to from 'await-to-js'
import { useEffect, useMemo, useState } from 'react'

import { DataTable, DataTableProvider } from 'components/DataTable'
import { TwoLineDateCell } from 'components/Tables/CommonTableCells/TwoLineDateCell'
import { InitialsAvatar } from 'components/Tables/CommonTableCells/InitialsAvatar'
import { IColumnVisibility } from 'components/DataTable/types'
import { TableSearch } from 'components/Filters/TableSearch'
import { FilterChips } from 'components/Filters/components/FilterChips/FilterChips'
import {
  filterStoreRepo,
  useFilterParams,
} from 'components/Filters/FilterStore'
import {
  useDeleteReminder,
  usePauseReminder,
  useUnpauseReminder,
} from 'services/reminders'
import useApiService from 'services/api'
import { ColumnsStoreProvider } from 'stores/ColumnsStore/ColumnsStoreProvider'
import * as S from 'components/Tables/CommonTable.styles'
import { DataTableContainer } from 'components/DataTable/UI'
import { getTableQueryKey } from '../../utils/getTableQueryKey'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { FilterIdentifier } from 'models/saved_view'
import { RemindersFilterset } from './RemindersFilterset'
import { ColumnSelectorRecipient } from 'components/Modals/ColumnModal/ColumnModal'
import { toast } from 'react-toastify'
import { ReminderListItem, ReminderStatus } from 'models/reminders'
import dialogConfirm from 'components/dialogConfirm'
import ReminderModal from './ReminderModal'
import { OverflownText } from 'components/OverflownText'
import { FbLink } from 'components/FbUI/FbLink'
import { cn } from 'components/UI/cn'
import { Clock, RefreshCw } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import {
  ActionsDropdown,
  createDeleteAction,
  createEditAction,
  createPauseAction,
  createPlayAction,
} from 'components/Buttons/ActionsDropdown'
import { reminderTypeFormat, statusColors } from './utils'
import { PillListCell } from 'components/Tables/CommonTableCells/PillListCell'
import { PillOverflowChip } from 'components/Tables/CommonTableCells/PillOverflowChip'
import { useTableLinkVariant } from 'components/Tables/CommonTableCells/TableLinkStyleContext'
import Checkbox from 'components/Checkbox/checkbox'

const PAGE_SIZE = 100

export function RemindersTable({
  tableKey,
  setTotalRowsCount,
}: {
  tableKey: string
  setTotalRowsCount?: (count: number) => void
}) {
  const api = useApiService()
  const deleteReminderMutation = useDeleteReminder()
  const pauseReminderMutation = usePauseReminder()
  const unpauseReminderMutation = useUnpauseReminder()
  const IDENTIFIER = 'REMINDERS_TABLE' as FilterIdentifier
  const reminderFilterStore = filterStoreRepo.getStore(IDENTIFIER)
  const { page, setPage } = reminderFilterStore()
  const filterParams = useFilterParams(reminderFilterStore)
  const { sorting } = dataTableSortingStoreRepo.getStore(tableKey)()
  const [editingReminderId, setEditingReminderId] = useState<number | null>(
    null
  )
  const [showInactive, setShowInactive] = useState(false)
  const linkVariant = useTableLinkVariant()

  const sortParams = useMemo(() => {
    const params: Record<string, unknown> = {}
    if (sorting?.length) {
      params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
    }
    return params
  }, [sorting])

  const filterAndSortParams = useMemo(() => {
    const params = {
      ...filterParams,
      ...sortParams,
    }

    // Add status exclusion when showSent is false
    if (!showInactive) {
      params['status__exclude'] = ['sent', 'ended', 'deleted', 'paused']
    }

    return params
  }, [filterParams, sortParams, showInactive])

  const TABLE_QUERY_KEY = getTableQueryKey({
    tableKey,
    filterParams: filterAndSortParams,
    page,
  })

  const { isFetching, data, refetch } = useQuery({
    queryKey: TABLE_QUERY_KEY,
    queryFn: async ({ signal }) => {
      return await api.getReminders(
        {
          ...filterAndSortParams,
          page,
          limit: PAGE_SIZE,
        },
        signal
      )
    },
    refetchOnMount: 'always',
  })

  useEffect(() => {
    setTotalRowsCount?.(data?.count ?? 0)
  }, [data?.count])

  async function handleReminderDelete(reminderId: number) {
    if (
      await dialogConfirm(
        <>
          Are you sure you want to delete this <b>reminder</b>?
        </>,
        'Delete Reminder?',
        '',
        { confirmLabel: 'Delete Reminder' }
      )
    ) {
      const [err] = await to(
        deleteReminderMutation.mutateAsync({ id: reminderId })
      )
      if (!err) {
        toast.success('Reminder deleted successfully')
        void refetch()
      } else {
        toast.error('Failed to delete reminder')
      }
    }
  }

  async function handleReminderPause(reminderId: number) {
    const [err] = await to(
      pauseReminderMutation.mutateAsync({ id: reminderId })
    )
    if (!err) {
      toast.success('Reminder paused successfully')
      void refetch()
    } else {
      toast.error('Failed to pause reminder')
    }
  }

  async function handleReminderPlay(reminderId: number) {
    const [err] = await to(
      unpauseReminderMutation.mutateAsync({ id: reminderId })
    )
    if (!err) {
      toast.success('Reminder now active')
      void refetch()
    } else {
      toast.error('Failed to play reminder')
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'title',
        header: 'Title',
        accessorKey: 'title',
        size: 260,
        meta: {
          headerGroup: 'Reminder Details',
        },
      },
      {
        id: 'content',
        header: 'Content',
        accessorKey: 'content',
        size: 400,
        meta: {
          headerGroup: 'Reminder Details',
        },
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const content = getValue() as string
          if (!content) {
            return <span className="text-gray-400">-</span>
          }
          if (linkVariant === 'plain') {
            // DS2026: 2-line clamp, click opens a scrollable popover with the
            // full content. Mirrors the OverflownScrollList pattern on Targets.
            return (
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="line-clamp-2 w-full min-w-0 max-w-full cursor-pointer break-words text-left hover:text-neutral-900"
                  >
                    {content}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="bottom"
                  align="center"
                  sideOffset={-32}
                  className="max-h-80 w-80 overflow-y-auto !p-3"
                >
                  <div className="whitespace-pre-wrap break-words text-sm text-neutral-700">
                    {content}
                  </div>
                </PopoverContent>
              </Popover>
            )
          }
          return (
            <OverflownText tooltipText={content} maxLines={2}>
              {content}
            </OverflownText>
          )
        },
      },
      {
        id: 'reminder_type',
        header: 'Frequency',
        accessorKey: 'reminder_type',
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const reminder_type = row.original.reminder_type

          return (
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                reminder_type === 'one_off'
                  ? 'bg-teal-100 text-teal-700'
                  : 'bg-green-100 text-green-700'
              )}
            >
              {reminder_type === 'one_off' ? (
                <Clock size={14} />
              ) : (
                <RefreshCw size={14} />
              )}
              {reminderTypeFormat(row.original)}
            </span>
          )
        },
        size: 140,
        meta: {
          headerGroup: 'Reminder Details',
        },
      },
      {
        id: 'run_at',
        header: 'Scheduled',
        accessorKey: 'run_at',
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const { reminder_type, run_at, next_run_at } = row.original

          const value = reminder_type === 'one_off' ? run_at : next_run_at
          return <TwoLineDateCell value={value} />
        },
        size: 120,
        meta: {
          headerGroup: 'Reminder Details',
          // Renders date over time — keep two lines.
          multiLine: true,
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'status',
        size: 120,
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const status = getValue() as ReminderStatus

          return (
            <span
              className={cn(
                'inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px]',
                statusColors[status] ?? 'bg-gray-100 text-gray-700'
              )}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
          )
        },
        meta: {
          headerGroup: 'Reminder Details',
        },
      },
      {
        id: 'end_date',
        header: 'End Date',
        accessorKey: 'end_date',
        size: 120,
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const endDate = row.original?.end_date

          if (!endDate || row.original.reminder_type === 'one_off') {
            return <span className="text-gray-400">-</span>
          }
          return <TwoLineDateCell value={endDate} />
        },
        meta: {
          headerGroup: 'Reminder Details',
          // Renders date over time — keep two lines.
          multiLine: true,
        },
      },
      {
        id: 'contact_company',
        header: 'Related Company',
        accessorKey: 'company_name',
        size: 220,
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const companyName = row.original?.company_name
          const companyLink = row.original?.company_notification_link

          if (!companyName || companyName === '') {
            return <span className="text-gray-400">-</span>
          }

          if (companyLink) {
            return (
              <div className="break-words whitespace-normal max-w-full overflow-hidden">
                <FbLink
                  to={companyLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block max-w-full break-words whitespace-normal overflow-visible"
                  style={{ textOverflow: 'clip' }}
                >
                  {companyName}
                </FbLink>
              </div>
            )
          }

          return companyName
        },
        meta: {
          headerGroup: 'Related Data',
        },
      },
      {
        id: 'door_proxy',
        header: 'Related Location',
        accessorKey: 'door_name',
        size: 220,
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const doorName = row.original?.door_name
          const doorLink = row.original?.door_notification_link

          if (!doorName || doorName === '') {
            return <span className="text-gray-400">-</span>
          }

          if (doorLink) {
            return (
              <div className="break-words whitespace-normal max-w-full overflow-hidden">
                <FbLink
                  to={doorLink}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-block max-w-full break-words whitespace-normal overflow-visible"
                  style={{ textOverflow: 'clip' }}
                >
                  {doorName}
                </FbLink>
              </div>
            )
          }

          return doorName
        },
        meta: {
          headerGroup: 'Related Data',
        },
      },
      {
        id: 'recipients',
        header: 'Recipients',
        accessorKey: 'recipients_names',
        size: 250,
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const names = row.original?.recipients_names || []
          if (names.length === 0) return <>-</>

          if (linkVariant === 'plain') {
            // DS2026: one truncated pill + clickable "+N" chip popover for the
            // rest. Matches the Samples products and Products variants columns.
            const [first, ...rest] = names
            return (
              <div className="flex items-center gap-1">
                <span
                  className="inline-flex items-center max-w-[180px] px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700 truncate"
                  title={first}
                >
                  {first}
                </span>
                {rest.length > 0 && (
                  <PillOverflowChip count={rest.length}>
                    {rest.map((name, i) => (
                      <span
                        key={i}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-2xl text-sm font-medium leading-5 tracking-[-0.2px] bg-gray-100 text-gray-700"
                      >
                        {name}
                      </span>
                    ))}
                  </PillOverflowChip>
                )}
              </div>
            )
          }

          return (
            <PillListCell
              items={names}
              maxColumnWidth={250}
              maxPillWidth={150}
            />
          )
        },
        meta: {
          headerGroup: 'Recipients',
        },
      },
      {
        id: 'created_by',
        header: 'Created By',
        accessorKey: 'created_by_name',
        size: 220,
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const name = ((getValue() as string | null) ?? '').trim()
          if (!name) return <>—</>
          const initials = name
            .split(' ')
            .map((n) => n[0] ?? '')
            .join('')
            .toUpperCase()
            .slice(0, 2)
          return (
            <div className="flex items-center gap-2.5">
              <InitialsAvatar initials={initials} />
              <span className="truncate">{name}</span>
            </div>
          )
        },
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'created',
        header: 'Date Created',
        accessorKey: 'created',
        size: 160,
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const value = getValue() as string
          if (!value) {
            return <span className="text-gray-400">-</span>
          }
          try {
            const date = new Date(value)
            if (isNaN(date.getTime())) {
              return <span className="text-gray-400">-</span>
            }
            return date.toLocaleDateString()
          } catch {
            return <span className="text-gray-400">-</span>
          }
        },
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'deleted_at',
        header: 'Date Deleted',
        accessorKey: 'deleted_at',
        size: 160,
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const value = getValue() as string
          if (!value) {
            return <span className="text-gray-400">-</span>
          }
          try {
            const date = new Date(value)
            if (isNaN(date.getTime())) {
              return <span className="text-gray-400">-</span>
            }
            return date.toLocaleDateString()
          } catch {
            return <span className="text-gray-400">-</span>
          }
        },
        meta: {
          headerGroup: 'Metadata',
        },
      },
      {
        id: 'integration_types',
        header: 'Integration',
        accessorKey: 'integration_types',
        size: 140,
        cell: ({ getValue }: CellContext<ReminderListItem, unknown>) => {
          const types = (getValue() as ('email' | 'calendar')[]) ?? []
          if (!types.length) return <span className="text-gray-400">-</span>
          return (
            <div className="flex gap-1">
              {types.includes('email') && (
                <span className="inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px] bg-blue-100 text-blue-700">
                  Email
                </span>
              )}
              {types.includes('calendar') && (
                <span className="inline-flex items-center rounded-2xl px-2.5 py-0.5 text-sm font-medium leading-5 tracking-[-0.2px] bg-purple-100 text-purple-700">
                  Calendar
                </span>
              )}
            </div>
          )
        },
        meta: {
          headerGroup: 'Reminder Details',
        },
      },
      {
        id: 'actions',
        header:
          linkVariant === 'plain'
            ? () => <div className="w-full text-center">Actions</div>
            : 'Actions',
        size: linkVariant === 'plain' ? 70 : 100,
        meta: {
          isEditable: false,
          headerDisableOrdering: true,
          isLastColumn: true,
          rightAlign: linkVariant !== 'plain',
          headerGroup: 'Actions',
        },
        cell: ({ row }: CellContext<ReminderListItem, unknown>) => {
          const reminder = row.original
          const actions = [
            createEditAction(() => setEditingReminderId(reminder.id)),
          ]

          if (
            reminder.reminder_type !== 'one_off' &&
            reminder.status === 'paused'
          ) {
            actions.push(
              createPlayAction(() => handleReminderPlay(reminder.id))
            )
          }

          if (
            reminder.reminder_type !== 'one_off' &&
            reminder.status === 'active'
          ) {
            actions.push(
              createPauseAction(() => handleReminderPause(reminder.id))
            )
          }

          actions.push(
            createDeleteAction(() => handleReminderDelete(reminder.id))
          )

          return (
            <ActionsDropdown
              disabled={reminder.status === 'deleted'}
              actions={actions}
              className={linkVariant === 'plain' ? 'justify-center' : undefined}
            />
          )
        },
      },
    ],
    [linkVariant]
  )

  return (
    <>
      <DataTableContainer>
        <DataTable
          tableHeader={
            <S.SearchContainer>
              <div className="flex w-full gap-2 items-center">
                <TableSearch
                  className={'flex-1'}
                  filterStore={reminderFilterStore}
                  searchPlaceholder="Search by title or content..."
                  tableKey={tableKey}
                />
                <RemindersFilterset filterIdentifier={IDENTIFIER} />
                <ColumnSelectorRecipient tableKey={tableKey} />
                <label className="flex cursor-pointer items-center gap-2">
                  <Checkbox
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                  <span
                    className="text-sm font-medium text-neutral-700"
                    style={{ letterSpacing: '-0.2px' }}
                  >
                    Show Inactive
                  </span>
                </label>
              </div>
              <FilterChips
                identifier={IDENTIFIER}
                store={reminderFilterStore}
                clearAllButton
                showActive
              />
            </S.SearchContainer>
          }
          loading={isFetching}
          tableKey={tableKey}
          data={data?.results ?? []}
          columns={columns as any}
          sortableFields={[
            'reminder_type',
            'run_at',
            'title',
            'status',
            'contact_company.name',
            'created_by',
            'created_at',
          ]}
          virtualizeRows
          enableColumnResizing={linkVariant === 'plain'}
          rowHeight={linkVariant === 'plain' ? 44 : undefined}
          headerHeight={linkVariant === 'plain' ? 40 : undefined}
          isPaginationEnabled
          stickyLastColumn
          paginationOptions={{
            totalRows: data?.count ?? 0,
            pageSize: PAGE_SIZE,
            setPage,
            page,
            isPaginationLoading: isFetching,
          }}
        />
      </DataTableContainer>
      {editingReminderId && (
        <ReminderModal
          open={!!editingReminderId}
          onClose={() => setEditingReminderId(null)}
          reminderId={editingReminderId}
          onUpdated={() => {
            setEditingReminderId(null)
            void refetch()
          }}
        />
      )}
    </>
  )
}

export function RemindersTableProvider(
  props: {
    tableKey: string
    setTotalRowsCount?: (count: number) => void
  } & {
    defaultColumnVisibility?: IColumnVisibility<any>
  }
) {
  const { tableKey, defaultColumnVisibility, ...rest } = props

  // Define default column visibility and order
  const defaultVisibility = defaultColumnVisibility || {
    reminder_type: true,
    run_at: true,
    title: true,
    content: true,
    status: true,
    contact_company: true,
    recipients: true,
    created_by: true,
    created: true,
    deleted_at: false, // Hidden by default
    integration_types: true,
    actions: true,
  }

  const initialColumnOrder = [
    'status',
    'run_at',
    'reminder_type',
    'title',
    'integration_types',
    'content',
    'recipients',
    'contact_company',
    'door_proxy',
    'created_by',
    'created',
    'end_date',
    'deleted_at',
    'actions',
  ]

  const preferredGroupOrder = [
    'Reminder Details',
    'Related Data',
    'Recipients',
    'Metadata',
    'Actions',
  ]

  return (
    <ColumnsStoreProvider
      tableKey={tableKey}
      savedPresetsIdentifier={'REMINDERS_TABLE' as FilterIdentifier}
      defaultColumnVisibility={defaultVisibility}
      initialColumnOrder={initialColumnOrder}
      preferredGroupOrder={preferredGroupOrder}
    >
      <DataTableProvider tableKey={tableKey}>
        <RemindersTable tableKey={tableKey} {...(rest as any)} />
      </DataTableProvider>
    </ColumnsStoreProvider>
  )
}
